import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// 生物模板专业版 - 细胞结构（横屏 1920×1080）
const THEME = {
  primary: '#00B894',
  primaryDark: '#009077',
  primaryLight: '#55EFC4',
  nucleus: '#9C27B0',
  membrane: '#1976D2',
  mito: '#FF9800',
  chloro: '#4CAF50',
  highlight: '#FFB347',
  bg: 'linear-gradient(135deg, #EAF8F2 0%, #E0F4EA 50%, #FFFFFF 100%)',
  cardShadow: '0 16px 40px rgba(0, 184, 148, 0.16)',
};

export const BiologyDemoPro: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: THEME.bg }}>
      <DecorativeBackground />

      <Sequence from={0} durationInFrames={150}>
        <IntroScene />
      </Sequence>
      <Sequence from={150} durationInFrames={150}>
        <TitleScene />
      </Sequence>
      <Sequence from={300} durationInFrames={1050}>
        <ExplanationScene />
      </Sequence>
      <Sequence from={1350} durationInFrames={300}>
        <QuizScene />
      </Sequence>
      <Sequence from={1650} durationInFrames={150}>
        <OutroScene />
      </Sequence>
    </AbsoluteFill>
  );
};

const DecorativeBackground: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill>
      {[
        { x: 150, y: 220, r: 160, c: THEME.primary, o: 0.06 },
        { x: 1720, y: 260, r: 200, c: THEME.nucleus, o: 0.05 },
        { x: 280, y: 880, r: 220, c: THEME.chloro, o: 0.05 },
        { x: 1680, y: 860, r: 140, c: THEME.mito, o: 0.05 },
      ].map((c, i) => {
        const float = Math.sin((frame + i * 35) * 0.025) * 14;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: c.x - c.r,
              top: c.y - c.r + float,
              width: c.r * 2,
              height: c.r * 2,
              borderRadius: '50%',
              background: c.c,
              opacity: c.o,
              filter: 'blur(24px)',
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const IntroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 120 } });
  const bob = Math.sin(frame * 0.15) * 10;
  const fadeText = interpolate(frame, [20, 50], [0, 1], { extrapolateRight: 'clamp' });
  const slideY = interpolate(frame, [20, 50], [30, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 240, transform: `scale(${scale}) translateY(${bob}px)` }}>🔬</div>
      <div style={{ opacity: fadeText, transform: `translateY(${slideY}px)` }}>
        <div style={{ fontSize: 120, fontWeight: 900, color: THEME.primary, letterSpacing: 4 }}>生物</div>
        <div style={{ fontSize: 32, color: '#555', marginTop: 16, letterSpacing: 6 }}>
          BIOLOGY · 微观世界
        </div>
      </div>
    </AbsoluteFill>
  );
};

const TitleScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const lineWidth = interpolate(frame, [30, 80], [0, 480], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '72px 120px',
          background: '#fff',
          borderRadius: 32,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 26, color: THEME.primary, letterSpacing: 8, marginBottom: 16 }}>
          CHAPTER · 04
        </div>
        <div style={{ fontSize: 72, fontWeight: 900, color: THEME.primary, marginBottom: 24 }}>
          细胞的结构与功能
        </div>
        <div style={{ fontSize: 28, color: '#555', fontWeight: 500 }}>
          生命活动的基本单位
        </div>
        <div
          style={{
            height: 4,
            width: lineWidth,
            background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.nucleus})`,
            margin: '24px auto 0',
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

const ExplanationScene: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={{ padding: 60, flexDirection: 'row', gap: 40, alignItems: 'center' }}>
      {/* 左侧：细胞剖面 */}
      <div
        style={{
          width: 1080,
          height: 820,
          background: '#fff',
          borderRadius: 24,
          position: 'relative',
          boxShadow: THEME.cardShadow,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '24px 32px', borderBottom: '1px solid #E8F8F3' }}>
          <div style={{ fontSize: 30, fontWeight: 700, color: THEME.primary }}>
            植物细胞剖面 · 4000× 显微镜视角
          </div>
          <div style={{ fontSize: 26, color: '#888', marginTop: 4 }}>
            细胞膜 · 细胞核 · 线粒体 · 叶绿体
          </div>
        </div>
        <CellStructureAnimation frame={frame} />
      </div>

      {/* 右侧：知识要点 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ fontSize: 24, color: THEME.primary, letterSpacing: 4, fontWeight: 600 }}>
          KNOWLEDGE POINTS
        </div>
        <div style={{ fontSize: 42, fontWeight: 900, color: '#1a1a1a', lineHeight: 1.2, marginBottom: 12 }}>
          细胞的四大结构
        </div>

        {[
          { title: '细胞膜', content: '控制物质进出，保护细胞完整', tag: '边界', color: THEME.membrane },
          { title: '细胞核', content: '遗传信息中心，调控细胞活动', tag: '指挥', color: THEME.nucleus },
          { title: '线粒体', content: '有氧呼吸场所，细胞能量工厂', tag: '能量', color: THEME.mito },
          { title: '叶绿体', content: '光合作用场所，合成有机物', tag: '光合', color: THEME.chloro },
        ].map((point, i) => {
          const start = i * 150 + 80;
          const opacity = interpolate(frame, [start, start + 25], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 25], [40, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                padding: '18px 24px',
                background: '#fff',
                borderRadius: 14,
                opacity,
                transform: `translateX(${x}px)`,
                boxShadow: '0 4px 16px rgba(0, 184, 148, 0.08)',
                borderLeft: `4px solid ${point.color}`,
                display: 'flex',
                alignItems: 'center',
                gap: 18,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 54,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${point.color}, ${point.color}AA)`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {point.tag}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#222', marginBottom: 2 }}>
                  {point.title}
                </div>
                <div style={{ fontSize: 22, color: '#666' }}>{point.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

const CellStructureAnimation: React.FC<{ frame: number }> = ({ frame }) => {
  const width = 1080;
  const height = 720;
  const cx = 540;
  const cy = 360;

  const cellScale = interpolate(frame, [0, 50], [0.6, 1], { extrapolateRight: 'clamp' });
  const membraneProgress = interpolate(frame, [40, 90], [0, 1], { extrapolateRight: 'clamp' });
  const nucleusProgress = interpolate(frame, [90, 150], [0, 1], { extrapolateRight: 'clamp' });
  const organelleProgress = interpolate(frame, [150, 240], [0, 1], { extrapolateRight: 'clamp' });
  const labelProgress = interpolate(frame, [240, 300], [0, 1], { extrapolateRight: 'clamp' });

  // 细胞膜脉动
  const pulse = 1 + Math.sin(frame * 0.08) * 0.02;

  // 线粒体位置（缓慢漂浮）
  const mitoFloat = (i: number) => Math.sin((frame + i * 40) * 0.06) * 8;

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <pattern id="bio-grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#EAF8F2" strokeWidth="1" />
        </pattern>
        <radialGradient id="cyto-grad" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#E3F2FD" />
          <stop offset="70%" stopColor="#BBDEFB" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#90CAF9" stopOpacity="0.6" />
        </radialGradient>
        <radialGradient id="nuc-grad" cx="0.3" cy="0.3" r="0.7">
          <stop offset="0%" stopColor="#CE93D8" />
          <stop offset="100%" stopColor="#7B1FA2" />
        </radialGradient>
        <radialGradient id="mito-grad" cx="0.3" cy="0.3" r="0.7">
          <stop offset="0%" stopColor="#FFD180" />
          <stop offset="100%" stopColor="#EF6C00" />
        </radialGradient>
        <radialGradient id="chloro-grad" cx="0.3" cy="0.3" r="0.7">
          <stop offset="0%" stopColor="#A5D6A7" />
          <stop offset="100%" stopColor="#2E7D32" />
        </radialGradient>
      </defs>

      <rect width={width} height={height} fill="url(#bio-grid)" />

      {/* 细胞质（整体） */}
      <ellipse cx={cx} cy={cy} rx={380 * cellScale * pulse} ry={260 * cellScale * pulse} fill="url(#cyto-grad)" />

      {/* 细胞膜（双层） */}
      {membraneProgress > 0 && (
        <>
          <ellipse
            cx={cx}
            cy={cy}
            rx={380 * pulse}
            ry={260 * pulse}
            fill="none"
            stroke={THEME.membrane}
            strokeWidth="5"
            opacity={membraneProgress}
          />
          <ellipse
            cx={cx}
            cy={cy}
            rx={370 * pulse}
            ry={252 * pulse}
            fill="none"
            stroke={THEME.membrane}
            strokeWidth="2"
            strokeDasharray="6,4"
            opacity={membraneProgress * 0.6}
          />
        </>
      )}

      {/* 内质网（波浪线） */}
      {organelleProgress > 0 && (
        <g opacity={organelleProgress * 0.7}>
          <path
            d={`M ${cx - 220} ${cy - 30} Q ${cx - 180} ${cy}, ${cx - 220} ${cy + 30} T ${cx - 220} ${cy + 90}`}
            fill="none"
            stroke="#9E9E9E"
            strokeWidth="3"
          />
          <path
            d={`M ${cx + 200} ${cy - 60} Q ${cx + 160} ${cy - 30}, ${cx + 200} ${cy} T ${cx + 200} ${cy + 60}`}
            fill="none"
            stroke="#9E9E9E"
            strokeWidth="3"
          />
        </g>
      )}

      {/* 叶绿体 */}
      {organelleProgress > 0 && (
        <>
          <g transform={`translate(${cx - 150}, ${cy + 120 + mitoFloat(0)}) rotate(20)`} opacity={organelleProgress}>
            <ellipse cx={0} cy={0} rx={52} ry={26} fill="url(#chloro-grad)" />
            <ellipse cx={-14} cy={-4} rx={8} ry={5} fill="#fff" opacity={0.3} />
            {[0, 1, 2, 3].map((k) => (
              <line key={k} x1={-32 + k * 18} y1={-10} x2={-28 + k * 18} y2={10} stroke="#1B5E20" strokeWidth="1.5" opacity={0.5} />
            ))}
          </g>
          <g transform={`translate(${cx + 170}, ${cy - 100 + mitoFloat(1)}) rotate(-15)`} opacity={organelleProgress}>
            <ellipse cx={0} cy={0} rx={48} ry={24} fill="url(#chloro-grad)" />
            <ellipse cx={-12} cy={-4} rx={7} ry={4} fill="#fff" opacity={0.3} />
          </g>
        </>
      )}

      {/* 线粒体 */}
      {organelleProgress > 0 && (
        <>
          <g transform={`translate(${cx - 200}, ${cy - 140 + mitoFloat(2)}) rotate(-30)`} opacity={organelleProgress}>
            <ellipse cx={0} cy={0} rx={45} ry={22} fill="url(#mito-grad)" />
            <path d="M -35 0 Q -25 -10, -15 0 T 5 0 T 25 0 T 40 0" fill="none" stroke="#E65100" strokeWidth="2" opacity={0.6} />
          </g>
          <g transform={`translate(${cx + 130}, ${cy + 140 + mitoFloat(3)}) rotate(40)`} opacity={organelleProgress}>
            <ellipse cx={0} cy={0} rx={40} ry={20} fill="url(#mito-grad)" />
            <path d="M -30 0 Q -20 -8, -10 0 T 10 0 T 30 0" fill="none" stroke="#E65100" strokeWidth="2" opacity={0.6} />
          </g>
        </>
      )}

      {/* 细胞核 */}
      {nucleusProgress > 0 && (
        <g opacity={nucleusProgress}>
          <circle cx={cx} cy={cy} r={80 * nucleusProgress} fill="url(#nuc-grad)" />
          <circle cx={cx} cy={cy} r={78 * nucleusProgress} fill="none" stroke="#4A148C" strokeWidth="2" opacity={0.5} />
          {/* 核仁 */}
          <circle cx={cx - 12} cy={cy - 8} r={18 * nucleusProgress} fill="#4A148C" opacity={0.8} />
          <circle cx={cx - 12} cy={cy - 12} r={6 * nucleusProgress} fill="#fff" opacity={0.3} />
          {/* 染色质小点 */}
          {[0, 1, 2, 3, 4].map((i) => {
            const angle = (i * 72) * Math.PI / 180;
            const r = 40;
            return (
              <circle
                key={i}
                cx={cx + Math.cos(angle + frame * 0.01) * r}
                cy={cy + Math.sin(angle + frame * 0.01) * r}
                r={3}
                fill="#4A148C"
                opacity={0.6}
              />
            );
          })}
        </g>
      )}

      {/* 标签 */}
      {labelProgress > 0 && (
        <g opacity={labelProgress}>
          <Label x1={cx + 80} y1={cy} x2={cx + 260} y2={cy - 180} color={THEME.nucleus} label="细胞核 Nucleus" sub="遗传调控中心" />
          <Label x1={cx - 270} y1={cy - 190} x2={cx - 160} y2={cy - 280} color={THEME.membrane} label="细胞膜 Membrane" sub="选择通透" alignRight />
          <Label x1={cx - 180} y1={cy - 120} x2={cx - 320} y2={cy - 60} color={THEME.mito} label="线粒体 Mito." sub="能量工厂 ATP" alignRight />
          <Label x1={cx - 100} y1={cy + 125} x2={cx - 280} y2={cy + 220} color={THEME.chloro} label="叶绿体 Chloro." sub="光合作用" alignRight />
        </g>
      )}

      {/* 显微镜指示 */}
      {frame > 360 && (
        <g opacity={interpolate(frame, [360, 400], [0, 1], { extrapolateRight: 'clamp' })}>
          <rect x={width - 200} y={20} width={180} height={44} rx={22} fill={THEME.primary} />
          <circle cx={width - 170} cy={42} r={10} fill="#fff" opacity={0.3} />
          <text x={width - 155} y={47} fontSize="18" fill="#fff" fontWeight="700">
            放大 4000×
          </text>
        </g>
      )}
    </svg>
  );
};

const Label: React.FC<{
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  label: string;
  sub: string;
  alignRight?: boolean;
}> = ({ x1, y1, x2, y2, color, label, sub, alignRight }) => {
  const boxW = 210;
  const boxH = 66;
  const boxX = alignRight ? x2 - boxW : x2;
  const textX = alignRight ? x2 - 16 : x2 + 16;
  const anchor = alignRight ? 'end' : 'start';
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.5" strokeDasharray="4,3" />
      <circle cx={x1} cy={y1} r={4} fill={color} />
      <rect x={boxX} y={y2 - boxH / 2} width={boxW} height={boxH} rx={12} fill="#fff" stroke={color} strokeWidth="1.5" />
      <text x={textX} y={y2 - 6} fontSize="22" fill={color} fontWeight="700" textAnchor={anchor}>
        {label}
      </text>
      <text x={textX} y={y2 + 20} fontSize="18" fill="#888" textAnchor={anchor}>
        {sub}
      </text>
    </g>
  );
};

const QuizScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
  const answerOpacity = interpolate(frame, [120, 160], [0, 1], { extrapolateRight: 'clamp' });
  const answerY = interpolate(frame, [120, 160], [20, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div
        style={{
          padding: '64px 96px',
          background: '#fff',
          borderRadius: 28,
          boxShadow: THEME.cardShadow,
          transform: `scale(${scale})`,
          minWidth: 960,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div
            style={{
              padding: '8px 20px',
              background: THEME.primary,
              color: '#fff',
              borderRadius: 20,
              fontSize: 26,
              fontWeight: 700,
            }}
          >
            Q · 互动测验
          </div>
          <div style={{ fontSize: 26, color: '#888' }}>识别细胞器的功能</div>
        </div>

        <div style={{ fontSize: 34, fontWeight: 700, color: '#1a1a1a', marginBottom: 40, lineHeight: 1.4 }}>
          被誉为细胞的
          <span style={{ color: THEME.mito }}>「能量工厂」</span>
          的细胞器是？
        </div>

        <div
          style={{
            opacity: answerOpacity,
            transform: `translateY(${answerY}px)`,
            padding: '28px 32px',
            background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
            borderRadius: 16,
            borderLeft: '6px solid #4CAF50',
          }}
        >
          <div style={{ fontSize: 28, color: '#4CAF50', fontWeight: 700, marginBottom: 8 }}>正确答案</div>
          <div style={{ fontSize: 28, color: '#2E7D32', fontWeight: 700 }}>
            线粒体（有氧呼吸的主要场所，合成 ATP）
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const OutroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 110 } });
  const textOpacity = interpolate(frame, [30, 70], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 80 }}>
      <div style={{ fontSize: 220, transform: `scale(${scale})` }}>🧬</div>
      <div style={{ opacity: textOpacity }}>
        <div style={{ fontSize: 100, fontWeight: 900, color: THEME.primary }}>掌握了！</div>
        <div style={{ fontSize: 30, color: '#555', marginTop: 16, letterSpacing: 4 }}>
          生命的奇妙从细胞开始
        </div>
      </div>
    </AbsoluteFill>
  );
};
